import { IonAvatar, IonBackButton, IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonContent, IonHeader, IonItem, IonLabel, IonPage, IonProgressBar, IonSpinner, IonText, IonTitle, IonToolbar, IonThumbnail, useIonAlert } from '@ionic/react';
import React, { memo, useContext } from "react";
import OCAuth from '../client/OCAuth';
import useUX from '../utils/ux';
import { NavContext } from '@ionic/react';
import { RouteComponentProps } from 'react-router-dom';
import { useAcceptDeviceUserInviteMutation, useGetDeviceUserInviteQuery } from '../query/deviceUserInviteQuery';
import styles from './AccountInvite.module.css';
import { format } from 'date-fns';
import { useCurrentUserQuery } from '../query/currentUserQuery';
import { useGatewayConnectionStateQuery } from '../query/gatewayConnectionStateQuery';

interface AccountInviteProps extends RouteComponentProps<{
    inviteToken: string;
}> { }

const AccountInvite: React.FC<AccountInviteProps> = ({ match }) => {
    const { inviteToken } = match.params;
    const { goBack } = useContext(NavContext);
    const currentUserQuery = useCurrentUserQuery();
    const gatewayConnectionStateQuery = useGatewayConnectionStateQuery();
    const user = currentUserQuery.data;
    const [presentAlert] = useIonAlert();
    const { presentResult } = useUX();
    const acceptInviteMutation = useAcceptDeviceUserInviteMutation();
    const inviteQuery = useGetDeviceUserInviteQuery(inviteToken);

    const login = async () => {
        await OCAuth.login();
    };

    const acceptInvitation = async () => {
        await presentAlert({
            header: 'Accept Invitation',
            message: 'Do you want to accept this device invitation?',
            buttons: [
                'Cancel',
                {
                    text: 'Accept',
                    handler: async () => {
                        presentResult(async () => {
                            const result = await acceptInviteMutation.mutateAsync(inviteToken);

                            goBack('/devices/' + result.deviceId);
                        }, "Device access granted");
                    }
                }
            ]
        });
    };

    const formatDate = (dateStr: string) => {
        try {
            return format(new Date(dateStr), 'P p');
        } catch (e) {
            return dateStr;
        }
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="/account" />
                    </IonButtons>
                    <IonTitle>Device Invitation</IonTitle>
                    {!!(gatewayConnectionStateQuery.isConnecting || currentUserQuery.isLoading) && <IonProgressBar type="indeterminate" />}
                </IonToolbar>
            </IonHeader>
            <IonContent className={styles.inviteContent} fullscreen>
                <IonCard>
                    <IonCardHeader>
                        <IonCardTitle>OnlyCat Device Invitation</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        {!user && <p>Sign in with your OnlyCat account to accept this device invitation.</p>}
                        {user && (
                            <>
                                <p>You've been invited to access an OnlyCat device.</p>
                            </>
                        )}
                        {inviteQuery.isError && (
                            <IonItem lines='none'>
                                <IonText color="danger">Error loading invitation details</IonText>
                            </IonItem>
                        )}
                        {inviteQuery.isLoading && (
                            <IonItem lines='none'>
                                <IonSpinner />
                                <IonText className="ion-padding-start">Loading invitation details...</IonText>
                            </IonItem>
                        )}
                        {inviteQuery.data && (
                            <div className="ion-margin-top">
                                <IonItem lines="none">
                                    <IonThumbnail slot="start" className={styles.inviteAvatar}>
                                        <img src="/assets/device/onlycat-device.png" alt="OnlyCat Device" />
                                    </IonThumbnail>
                                    <IonLabel>
                                        <h2>Device</h2>
                                        <h4>{inviteQuery.data.device.deviceId}</h4>
                                        <p>{inviteQuery.data.device.description}</p>
                                    </IonLabel>
                                </IonItem>
                                <IonItem lines="none">
                                    <IonAvatar slot="start" className={styles.inviteAvatar}>
                                        <img
                                            src={inviteQuery.data.invitingUser.avatarUrl || '/assets/account/default-avatar.svg'}
                                            alt="Inviting User Avatar"
                                            referrerPolicy="no-referrer"
                                        />
                                    </IonAvatar>
                                    <IonLabel>
                                        <h2>Invited by</h2>
                                        <p>{inviteQuery.data.invitingUser.name || inviteQuery.data.invitingUser.email}</p>
                                    </IonLabel>
                                </IonItem>
                                {user && (
                                    <IonItem lines="none">
                                        <IonAvatar slot="start" className={styles.inviteAvatar}>
                                            <img
                                                src={user?.avatarUrl ?? '/assets/account/default-avatar.svg'}
                                                alt={user?.name ?? 'Guest'}
                                                referrerPolicy="no-referrer"
                                            />
                                        </IonAvatar>
                                        <IonLabel>
                                            <h2>Your Account</h2>
                                            <p>{user?.name ?? "Not Signed In"}</p>
                                            <p>{user?.email}</p>
                                        </IonLabel>
                                    </IonItem>
                                )}
                                <IonItem lines="none">
                                    <IonLabel>
                                        <h2>Expires</h2>
                                        <p>{formatDate(inviteQuery.data.expiresAt)}</p>
                                    </IonLabel>
                                </IonItem>
                            </div>
                        )}
                    </IonCardContent>

                    {!user && <IonButton onClick={login} fill="clear" color="primary">
                        Sign In to Accept Invitation
                    </IonButton>}

                    {user && !acceptInviteMutation.isSuccess && <IonButton onClick={acceptInvitation} fill="clear" disabled={!acceptInviteMutation.isIdle}>
                        Accept Invitation
                    </IonButton>}
                </IonCard>
            </IonContent>
        </IonPage >
    );
};

export default memo(AccountInvite);
